import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import type { VerificationInfo } from 'webapp/utils/verification';

import highlightResult from 'webapp/helpers/highlight-result';
import joinClasses from 'webapp/helpers/join-classes';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { MetricItem } from 'webapp/components/form/metric-select/popup/index';
import MetricLibraryPopupsMetricBasicInfoTooltipComponent from 'webapp/components/metric-library/popups/metric-basic-info-tooltip';
import ScrollableTextComponent from 'webapp/components/scrollable-text';
import TagContainerComponent from 'webapp/components/tag-container';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

import { MIN_CHARS_FOR_DIMENSIONS_FILTER } from 'webapp/models-custom/filter-sort/metric-select';

interface Args {
	model: MetricItem;
	isSelected: boolean;
	theme?: string;
	hideFavoriteBtn?: boolean;
	hideConnectBtn?: boolean;
	originForMetricFavorite?: string;
}

export default class FormMetricSelectPopupListItemComponent extends Component<Args> {
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked isPopupOpen = false;

	get isFavorite() {
		return this.args.model.isFavorite;
	}

	get verificationInfo(): VerificationInfo | undefined {
		return 'verificationInfo' in this.args.model ? this.args.model.verificationInfo : undefined;
	}

	@cached
	get matchingDimensions(): string[] {
		const activeFilter = this.args.model.activeFilter,
			activeFilterString = activeFilter.join(' ');

		if (!activeFilterString || activeFilterString.length < MIN_CHARS_FOR_DIMENSIONS_FILTER) {
			return [];
		}

		return this.args.model.dimensions
			.filter((dimension) => {
				const dimensionValue = dimension.value.toLowerCase();
				return activeFilter.some((filter: string) => {
					return dimensionValue.includes(filter.toLowerCase());
				});
			})
			.map((dimension) => dimension.value);
	}

	get matchingDimensionsString() {
		return this.matchingDimensions.join(', ');
	}

	get showDimensionsLine() {
		// do not show the dimensions line if it does not include the search term (that must be > min chars too)
		return this.args.model.allowDimensions && this.matchingDimensions.length;
	}

	get metricName(): string {
		const model = this.args.model;
		let name = model.name;
		if (model.isDeprecated) {
			name += ' (deprecated)';
		}
		return name;
	}

	get metricTypeInfos() {
		const metricTypeInfos: { title: string; icon?: IconName }[] = [];

		const metric = this.args.model;

		if (metric.isTypeMetricCalculationOrGeneric) {
			metricTypeInfos.push({
				title: 'Calculated metric',
			});
		} else if (metric.isTypeBuilt) {
			metricTypeInfos.push({
				title: 'Built metric',
			});
		}

		if (metric.allowDimensions && !this.showDimensionsLine) {
			metricTypeInfos.push({
				icon: 'Breakdown',
				title: metric.dimensionsString,
			});
		}

		return metricTypeInfos;
	}

	get isSourceErrorWarningShown(): boolean {
		return this.statusObject?.level === 'error';
	}

	get sourceTitle(): string {
		return this.args.model.sourceName;
	}

	get statusObject() {
		return this.args.model.source?.statusObject || null;
	}

	get isFavoriteBtnShown(): boolean {
		return (
			this.mmMetricsService.showFavoriteAction &&
			!this.args.hideFavoriteBtn &&
			!!this.args.model?.source &&
			!this.args.model?.mustResolveBeforeSelecting
		);
	}

	@cached
	get modelWithTags() {
		const tagsObjs = this.args.model.tagRecords;
		return tagsObjs
			? {
					tags: [], // not needed, since its for view only
					tagsObjs,
				}
			: null;
	}

	// Only shown while mmMetricsService.showFavoriteAction is on; favoriting moves onto the registry in M2.
	@action
	favoriteMetric(event: Event): void {
		event.stopPropagation(); // prevent triggering selectItem in PopupMetricSelectController
	}
	<template>
		<div
			class={{joinClasses
				(array
					"item"
					(if @isSelected "item--selected")
					(if this.isFavorite "item--favorited")
					(unless this.modelWithTags "item--no-tags")
				)
			}}
		>
			<div class="item__favorite">
				{{#if this.isFavoriteBtnShown}}
					<a role="button" {{on "click" this.favoriteMetric}}>
						<CommonUiIconComponent
							@name={{if this.isFavorite "StarFill" "Star"}}
							@size="sm"
						/>
					</a>
				{{/if}}
			</div>
			<span
				class="item__name_wrapper
					{{if this.showDimensionsLine 'item__name_wrapper--two-lines'}}"
			>
				<span class="item__name">
					<span class="label">
						<ScrollableTextComponent>
							{{highlightResult text=this.metricName match=@model.activeFilter}}
						</ScrollableTextComponent>
					</span>
					{{#each this.metricTypeInfos as |metricTypeInfo|}}
						{{#if metricTypeInfo.icon}}
							<span class="metric-type-icon" title={{metricTypeInfo.title}}>
								<CommonUiIconComponent @name={{metricTypeInfo.icon}} @size="sm" />
							</span>
						{{/if}}
					{{/each}}
					<VerificationBadgeComponent
						@verification={{this.verificationInfo}}
						@entityLabel="metric"
						@size="sm"
					/>
				</span>
				{{#if this.showDimensionsLine}}
					<span class="item__dimensions">
						<CommonUiIconComponent @name="Breakdown" @size="sm" />
						<span class="label">
							<ScrollableTextComponent>
								{{highlightResult
									text=this.matchingDimensionsString
									match=@model.activeFilter
								}}
							</ScrollableTextComponent>
						</span>
					</span>
				{{/if}}
			</span>
			<div class="item__source">
				<img src={{@model.sourceAvatar}} alt />
				<span class="label">
					<ScrollableTextComponent>
						{{highlightResult text=this.sourceTitle match=@model.activeFilter}}
					</ScrollableTextComponent>
				</span>
				{{#if @model.source}}
					{{#if this.isSourceErrorWarningShown}}
						<ToolTipWithIconComponent
							@text={{this.statusObject.statusTitleAndDescriptionWithAction}}
							@theme={{@theme}}
							@iconName="AlertFill"
							@maxWidth={{235}}
						/>
					{{else}}
						{{#unless @model.mustResolveBeforeSelecting}}
							<MetricLibraryPopupsMetricBasicInfoTooltipComponent
								@model={{@model}}
								@mouseLeaveDelay={{500}}
							>
								<CommonUiIconComponent @name="InfoFill" @size="sm" />
							</MetricLibraryPopupsMetricBasicInfoTooltipComponent>
						{{/unless}}
					{{/if}}
					{{#if @model.mustResolveBeforeSelecting}}
						<a role="button" class="button size-tiny">Create</a>
					{{/if}}
				{{else}}
					{{#unless @hideConnectBtn}}
						<a role="button" class="button theme-green size-tiny action--connect">
							<CommonUiIconComponent @name="Connect" @size="sm" />Connect
						</a>
					{{/unless}}
				{{/if}}
			</div>
			<div class="item__tags">
				{{#if this.modelWithTags}}
					<TagContainerComponent
						@model={{this.modelWithTags}}
						@canEdit={{false}}
						@showSingleTagName={{false}}
						@maxShownTags={{5}}
					/>
				{{/if}}
			</div>
		</div>
	</template>
}
